import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SetterOrUpdater } from "recoil";

import {
  DetailedRequisitionSerializer,
  RequisitionModelSerializer,
  RequisitionRow,
  UserDefaultRequisitionsResponse,
  UserSearchRequisitionsResponse,
} from "../../recoil/types";
import { AlertTab, CfcEligiblePatient } from "../Cfc/CfcEligibilityModal";
import { CfcPatientData } from "../Cfc/types";
import { CFC_ELIGIBILITY_ALERT_AUTO_OPENED_SESSION_KEY } from "../Cfc/constants";
import {
  buildCfcPatientDataFromDetailedRequisition,
  buildCfcPatientDataFromRow,
  isEligibleForCfc,
} from "../Cfc/cfcGridUtils";
import { DismissCfcAlert } from "./Services/RequisitionAlertService";
import { GetSpecificRequisition } from "./Services/GridService";
import { Toast } from "./useToast";

type RequisitionCollection =
  | UserDefaultRequisitionsResponse
  | UserSearchRequisitionsResponse;

const markCfcAlertDismissed = (
  requisitions: RequisitionCollection | undefined,
  reqID: string,
): RequisitionCollection | undefined => {
  if (!requisitions) return requisitions;

  const markReq = (requisition: RequisitionModelSerializer) =>
    requisition.req_identifier === reqID
      ? { ...requisition, is_cfc_alert_dismissed: true }
      : requisition;

  return {
    ...requisitions,
    requisitions_completed: requisitions.requisitions_completed.map(markReq),
    requisitions_processing: requisitions.requisitions_processing.map(markReq),
  };
};

interface UseCfcAlertsParams {
  canUseProviderCfc: boolean;
  completedRows: RequisitionRow[];
  processingRows: RequisitionRow[];
  token?: string;
  username?: string;
  setLoggedOut: (inactivity: boolean) => void;
  toast: Toast;
  refreshVisibleRequisitionData: () => Promise<void>;
  refreshAfterCfcOrderSubmitted: () => void;
  setRequisitions: SetterOrUpdater<UserDefaultRequisitionsResponse | undefined>;
  setSearchRequisitions: SetterOrUpdater<
    UserSearchRequisitionsResponse | undefined
  >;
  setAttestationModalOpen: Dispatch<SetStateAction<boolean>>;
}

export interface CfcAlerts {
  cfcEligiblePatients: CfcEligiblePatient[];
  cfcModalOpen: boolean;
  setCfcModalOpen: Dispatch<SetStateAction<boolean>>;
  cfcModalInitialTab: AlertTab;
  setCfcModalInitialTab: Dispatch<SetStateAction<AlertTab>>;
  cfcOrderModalData: CfcPatientData | null;
  setCfcOrderModalData: Dispatch<SetStateAction<CfcPatientData | null>>;
  handleCfcOrderFromAlert: (row: RequisitionRow) => void;
  handleCfcMuteFromAlert: (row: RequisitionRow) => Promise<void>;
  handleCfcOrderSubmitted: () => void;
  getGlobalSearchCfcOrderData: (sfid: string) => Promise<CfcPatientData | null>;
  handleGlobalSearchCfcOrder: (patientData: CfcPatientData) => void;
}

export const useCfcAlerts = ({
  canUseProviderCfc,
  completedRows,
  processingRows,
  token,
  username,
  setLoggedOut,
  toast,
  refreshVisibleRequisitionData,
  refreshAfterCfcOrderSubmitted,
  setRequisitions,
  setSearchRequisitions,
  setAttestationModalOpen,
}: UseCfcAlertsParams): CfcAlerts => {
  const [cfcModalOpen, setCfcModalOpen] = useState(false);
  const [cfcModalInitialTab, setCfcModalInitialTab] = useState<AlertTab>(
    AlertTab.UnityConfirmEligibility,
  );
  const [cfcOrderModalData, setCfcOrderModalData] =
    useState<CfcPatientData | null>(null);

  const cfcEligiblePatients: CfcEligiblePatient[] = useMemo(() => {
    if (!canUseProviderCfc) return [];

    const allRows = [...completedRows, ...processingRows];
    return allRows
      .filter((row) => isEligibleForCfc(row, { requireConditions: true }))
      .map((row) => ({
        reqIdentifier: row.reqID,
        patientName: row.name,
        collectByDate: row.cfcEligibleCollectByDate ?? "",
        rowData: row,
      }));
  }, [canUseProviderCfc, completedRows, processingRows]);

  const undismissedCfcAlertCount = useMemo(
    () =>
      cfcEligiblePatients.filter(
        (patient) => !patient.rowData.isCfcAlertDismissed,
      ).length,
    [cfcEligiblePatients],
  );

  useEffect(() => {
    const autoOpenedSessionValue = username ?? "provider";
    const hasAutoOpenedCfcEligibilityModal =
      sessionStorage.getItem(CFC_ELIGIBILITY_ALERT_AUTO_OPENED_SESSION_KEY) ===
      autoOpenedSessionValue;

    if (
      !canUseProviderCfc ||
      undismissedCfcAlertCount === 0 ||
      hasAutoOpenedCfcEligibilityModal ||
      cfcModalOpen ||
      cfcOrderModalData
    ) {
      return;
    }

    sessionStorage.setItem(
      CFC_ELIGIBILITY_ALERT_AUTO_OPENED_SESSION_KEY,
      autoOpenedSessionValue,
    );
    setCfcModalInitialTab(AlertTab.UnityConfirmEligibility);
    setCfcModalOpen(true);
  }, [
    username,
    canUseProviderCfc,
    undismissedCfcAlertCount,
    cfcModalOpen,
    cfcOrderModalData,
  ]);

  const handleCfcOrderFromAlert = (row: RequisitionRow) => {
    if (!canUseProviderCfc) return;
    setCfcModalOpen(false);
    setCfcOrderModalData(buildCfcPatientDataFromRow(row));
  };

  const handleCfcMuteFromAlert = async (row: RequisitionRow) => {
    if (!canUseProviderCfc || !token) return;

    setRequisitions((prev) => markCfcAlertDismissed(prev, row.reqID));
    setSearchRequisitions((prev) => markCfcAlertDismissed(prev, row.reqID));

    toast.showToast(
      "Alerts stopped for this patient. You won't see automatic pop-ups for them anymore.",
    );

    try {
      const response = await DismissCfcAlert(token, row.reqID, setLoggedOut);
      if (!response.ok) throw new Error("Failed to mute alert");
    } catch {
      await refreshVisibleRequisitionData();
      toast.showError("Could not mute alert. Please try again.");
    }
  };

  const getGlobalSearchCfcOrderData = useCallback(
    async (sfid: string): Promise<CfcPatientData | null> => {
      if (!token || !canUseProviderCfc) return null;
      const response = await GetSpecificRequisition(token, sfid, setLoggedOut);
      if (!response.ok) return null;

      const requisition =
        (await response.json()) as DetailedRequisitionSerializer;
      if (!isEligibleForCfc(requisition, { requireConditions: true })) {
        return null;
      }

      return buildCfcPatientDataFromDetailedRequisition(requisition);
    },
    [token, canUseProviderCfc, setLoggedOut],
  );

  const handleGlobalSearchCfcOrder = useCallback(
    (patientData: CfcPatientData) => {
      setAttestationModalOpen(false);
      setCfcOrderModalData(patientData);
    },
    [setAttestationModalOpen],
  );

  const handleCfcOrderSubmitted = () => {
    refreshAfterCfcOrderSubmitted();
  };

  return {
    cfcEligiblePatients,
    cfcModalOpen,
    setCfcModalOpen,
    cfcModalInitialTab,
    setCfcModalInitialTab,
    cfcOrderModalData,
    setCfcOrderModalData,
    handleCfcOrderFromAlert,
    handleCfcMuteFromAlert,
    handleCfcOrderSubmitted,
    getGlobalSearchCfcOrderData,
    handleGlobalSearchCfcOrder,
  };
};
